import { Copy } from "@phosphor-icons/react";
import { useRef } from "react";
import { Button } from "src/components/Buttons/Button";
import { CardContainer } from "src/components/Layout/CardContainer";
import { ConfirmationModalButton } from "src/components/Modal/ConfirmationModalButton";
import { usePendingOrdersController } from "../hooks/usePendingOrdersController";
import { StatementRedisPanel, StatementRedisPanelHandle } from "./Gowd/Extrato/StatementRedisPanel";
import { PixToolModal } from "./Gowd/Pix/PixToolModal";
import { OrderMessages } from "./OrderMessages";
import { BinanceChatBox } from "./PendingOrders/Chat/BinanceChatBox";
import { ChatBox } from "./PendingOrders/Chat/ChatBox";
import { GateChatBox } from "./PendingOrders/Chat/GateChatBox";
import { MexcChatBox } from "./PendingOrders/Chat/MexcChatBox";
import { CompliancePopover } from "./PendingOrders/CompliancePopover";
import { PaymentTermsBox } from "./PendingOrders/PaymentTermsBox";
import { TABS } from "./PendingOrders/utils/pendingOrdersConfig";
import {
  brl,
  canActByStatus,
  complianceState,
  getEndToEnd,
  getOrdersByTab,
  isBinance,
  isBitget,
  isBotCancel,
  isBybit,
  isCpfCnpj,
  isGate,
  isMexc,
  onlyDigits,
  statusLabel,
} from "./PendingOrders/utils/pendingOrdersHelpers";
import { PendingOrdersProps } from "./PendingOrders/utils/pendingOrdersTypes";
export type KeyType = "empresa" | "pessoal";
export const PendingOrders = ({ setForm, setInitialRegisterData }: PendingOrdersProps) => {
  const {
    acesso,
    activeBybitKeyType,
    activeConfig,
    activeTab,
    changeTab,
    closePixModal,
    closeModal,
    data,
    error,
    handleConfirm,
    handlePixSuccess,
    isChatBinancePending,
    isLoading,
    isMarkPaidBybitPending,
    isMarkPaidBinancePending,
    isReleaseBinancePending,
    isMarkPaidBitgetPending,
    isReleaseBitgetPending,
    isMarkPaidMexcPending,
    isReleaseMexcPending,
    isMarkPaidGatePending,
    isReleaseGatePending,
    modalAction,
    openActionModal,
    openPixModal,
    openedComplianceOrderId,
    orders,
    pixModalInitialValues,
    selectedOrder,
    sendChatBinance,
    setOpenedComplianceOrderId,
    showModal,
  } = usePendingOrdersController();
  const statementRedisRef = useRef<StatementRedisPanelHandle>(null);
  const handleConfirmWithRedisVerification = async () => {
    const ok = await statementRedisRef.current?.verifySelectedTrue();
    if (ok === false) return;
    handleConfirm();
  };
  if (isLoading) return <p>Carregando ordens...</p>;
  if (error) return <p>Erro ao carregar ordens.</p>;
  if (!data) return <p>Sem ordens pendentes.</p>;
  return (
    <CardContainer full>
      <h3 className="text-28 font-bold">ORDENS PENDENTES</h3>
      <div className="flex flex-wrap gap-2">
        {TABS.map(({ tab, label }) => {
          const orderCount = getOrdersByTab(data, tab).length;
          return (
            <div key={tab} className="relative">
              <Button
                onClick={() => changeTab(tab)}
                className={`rounded-6 p-2 ${
                  activeTab === tab ? "bg-gray-400 text-white" : "bg-gray-200"
                }`}
              >
                {label}
              </Button>
              {orderCount > 0 && (
                <span
                  className="pointer-events-none absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white"
                  aria-label={`${orderCount} ordens pendentes em ${label}`}
                >
                  {orderCount}
                </span>
              )}
            </div>
          );
        })}
      </div>
      {orders.length === 0 ? (
        <p>Sem ordens em {activeConfig.label}</p>
      ) : (
        <div className="mt-2 flex flex-wrap gap-2 overflow-visible">
          {orders.map((order) => {
            const isBuyOrder = Number(order.side) === 0;
            const compliance = (order.compliance ?? null) as Record<string, unknown> | null;
            const orderDocument = String(order.documento ?? "");
            const complianceDocument = String(compliance?.documento ?? "");
            const documento = isCpfCnpj(onlyDigits(orderDocument))
              ? orderDocument
              : isCpfCnpj(onlyDigits(complianceDocument))
                ? complianceDocument
                : "";
            const mensagens = Array.isArray(order.mensagens) ? order.mensagens : [];
            const pagamento = Array.isArray(order.pagamento) ? order.pagamento : [];
            const [cardClass, badgeClass, badgeLabel] = complianceState(compliance);
            const isPendingAny =
              isMarkPaidBybitPending ||
              isMarkPaidBinancePending ||
              isMarkPaidBitgetPending ||
              isMarkPaidMexcPending ||
              isMarkPaidGatePending ||
              isReleaseBinancePending ||
              isReleaseBitgetPending ||
              isReleaseBitgetPending ||
              isReleaseMexcPending ||
              isReleaseGatePending;
            const requiresMessages = !isBitget(activeConfig);
            const disabledAction =
              isPendingAny ||
              acesso !== "Master" ||
              !isCpfCnpj(documento) ||
              (requiresMessages && mensagens.slice(0).reverse().slice(-10).some(isBotCancel)) ||
              !canActByStatus(activeConfig, order, isBuyOrder);
            return (
              <div
                key={`${String(order.keyType ?? activeConfig.keyType)}-${String(order.id)}`}
                className={`relative flex w-fit flex-col gap-0.5 rounded-xl border p-4 pt-10 shadow ${cardClass}`}
              >
                {isBuyOrder && pagamento.length > 0 && (
                  <PaymentTermsBox terms={pagamento} title="Dados para pagamento" />
                )}
                <button
                  className="absolute right-2 top-2 rounded-6 border border-gray-200 bg-white p-2 hover:bg-gray-100 hover:opacity-80"
                  onClick={() => {
                    setInitialRegisterData({
                      apelido: String(order.apelido || ""),
                      nome: String(isBuyOrder ? order.vendedor : order.comprador),
                      exchange: activeConfig.registerExchange,
                    });
                    setForm(true);
                  }}
                  title="Copiar para cadastro"
                >
                  <Copy width={20} height={20} weight="duotone" />
                </button>
                {badgeLabel && (
                  <span
                    className={`absolute left-2 top-2 rounded-full px-2 py-1 text-xs font-semibold ${badgeClass}`}
                  >
                    {badgeLabel}
                  </span>
                )}
                {isCpfCnpj(documento) && compliance && (
                  <button
                    type="button"
                    className="absolute right-14 top-2 rounded-6 border border-gray-200 bg-white px-2 py-1 text-xs hover:bg-gray-100"
                    onClick={() =>
                      setOpenedComplianceOrderId((prev) =>
                        prev === String(order.id) ? null : String(order.id),
                      )
                    }
                  >
                    Compliance
                  </button>
                )}
                {isCpfCnpj(documento) &&
                  isBuyOrder &&
                  canActByStatus(activeConfig, order, isBuyOrder) && (
                    <button
                      type="button"
                      className="absolute right-2 top-12 rounded-6 border border-green-200 bg-green-50 px-4 py-2 text-16 font-semibold text-green-700 hover:bg-green-100"
                      onClick={() => openPixModal(order)}
                    >
                      Pix
                    </button>
                  )}
                <p>
                  <strong>Exchange:</strong> {activeConfig.exchangeName}
                </p>
                <p>
                  <strong>ID da Ordem:</strong> {String(order.id ?? "-")}
                </p>
                {order.advNo ? (
                  <p>
                    <strong>Adv No:</strong> {String(order.advNo)}
                  </p>
                ) : null}
                <p>
                  <strong>Data:</strong> {String(order.data || "N/A")}
                </p>
                <p>
                  <strong>Status:</strong> {statusLabel(activeConfig, order.status)}
                </p>
                <p>
                  <strong>Apelido:</strong> {String(order.apelido || "Não informado")}
                </p>
                <p>
                  <strong>Nome:</strong>{" "}
                  {String((isBuyOrder ? order.vendedor : order.comprador) || "Não informado")}
                </p>
                <p>
                  <strong>Tipo:</strong> {isBuyOrder ? "compras" : "vendas"}
                </p>
                <p>
                  <strong>Quantidade:</strong> {String(order.quantidade ?? "-")}{" "}
                  {String(order.token ?? "")}
                </p>
                <p>
                  <strong>Valor:</strong> {String(order.valor ?? "-")} {String(order.moeda ?? "")}
                </p>
                <p>
                  <strong>Preço Unitário:</strong> {brl(order.preco)} {String(order.moeda ?? "")}
                </p>
                <p>
                  <strong>CPF/CNPJ:</strong> {documento || "Não informado"}
                </p>
                {onlyDigits(documento).length === 11 && getEndToEnd(order.endtoend) && (
                  <p>
                    <strong>EndToEnd:</strong> {getEndToEnd(order.endtoend)}
                  </p>
                )}
                {mensagens.length > 0 && <OrderMessages messages={mensagens} />}
                {isBybit(activeConfig) && (
                  <ChatBox orderId={String(order.id)} keyType={activeBybitKeyType} />
                )}
                {isBinance(activeConfig) && (
                  <BinanceChatBox
                    orderId={String(order.id)}
                    isPending={isChatBinancePending}
                    sendChatBinance={sendChatBinance}
                  />
                )}
                {isMexc(activeConfig) && (
                  <MexcChatBox
                    orderId={String(order.id)}
                    keyType={activeConfig.keyType === "pessoal" ? "pessoal" : "empresa"}
                  />
                )}
                {isGate(activeConfig) && (
                  <GateChatBox
                    orderId={String(order.id)}
                    keyType={activeConfig.keyType === "pessoal" ? "pessoal" : "empresa"}
                  />
                )}
                <Button
                  disabled={disabledAction}
                  onClick={() => openActionModal(order, isBuyOrder ? "markPaid" : "release")}
                >
                  {statusLabel(activeConfig, order.status)}
                </Button>
                {openedComplianceOrderId === String(order.id) && compliance && (
                  <div className="absolute left-[calc(100%+12px)] top-0 z-50">
                    <CompliancePopover
                      data={compliance}
                      documento={documento}
                      onClose={() => setOpenedComplianceOrderId(null)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {showModal && selectedOrder && (
        <ConfirmationModalButton
          text={`${
            modalAction === "release"
              ? `Está certo que deseja liberar para ${String(selectedOrder.comprador ?? "")} `
              : `Está certo que já fez o pagamento para ${String(selectedOrder.vendedor ?? "")} `
          }a quantidade de ${String(selectedOrder.quantidade ?? "")} ${String(
            selectedOrder.token ?? "",
          )} no valor de ${String(selectedOrder.valor ?? "")} ${String(selectedOrder.moeda ?? "")}?`}
          onConfirm={handleConfirmWithRedisVerification}
          onCancel={closeModal}
          showExtra
          extra={
            <StatementRedisPanel
              ref={statementRedisRef}
              autoSelectEndToEnd={getEndToEnd(selectedOrder.endtoend)}
              hideVerifyButton
            />
          }
        />
      )}
      {pixModalInitialValues && (
        <PixToolModal
          initialValues={pixModalInitialValues}
          onClose={closePixModal}
          onPixSuccess={handlePixSuccess}
        />
      )}
    </CardContainer>
  );
};
